/**
 * Vue plugin for the Adobe Express Embed SDK.
 *
 * Registers config so components can access the SDK via the useAdobeExpressEmbed()
 * composable. The SDK script is loaded and initialized on first use (singleton).
 *
 * @see https://developer.adobe.com/express/embed-sdk/docs
 *
 * @example
 * // main.js - use defaults from env (VITE_ADOBE_EXPRESS_CLIENT_ID, VITE_BRANDING_APP_NAME)
 * import AdobeExpressEmbedPlugin from '@/plugins/adobe-express-embed';
 * app.use(AdobeExpressEmbedPlugin);
 *
 * @example
 * // main.js - override specific options
 * app.use(AdobeExpressEmbedPlugin, { appName: 'My App' });
 *
 * @example
 * // In a component
 * const { editor, isReady, error, ensureReady } = useAdobeExpressEmbed();
 * await ensureReady();
 * editor.create(docConfig, appConfig, exportConfig);
 */

import { env } from '@/env';

const ADOBE_EXPRESS_EMBED_CONFIG = Symbol('adobeExpressEmbedConfig');

const SDK_CDN_URL = 'https://cc-embed.adobe.com/sdk/v4/CCEverywhere.js';

const defaultConfig = {
  clientId: env.adobeExpressClientId ?? '',
  appName: env.brandingAppName ?? 'Dash Social',
  appVersion: { major: 1, minor: 0 },
  platformCategory: 'web',
  locale: 'en_US',
  loginMode: 'delayed',
};

/**
 * @param {import('vue').App} app
 * @param {{
 *   clientId?: string;
 *   appName?: string;
 *   appVersion?: { major: number; minor: number };
 *   platformCategory?: string;
 *   locale?: string;
 *   loginMode?: 'immediate' | 'delayed';
 * }} [options] - Optional overrides. Defaults use env.adobeExpressClientId, env.brandingAppName, and the standard SDK defaults.
 */
function install(app, options = {}) {
  const config = { ...defaultConfig, ...options };
  if (!config.clientId || !config.appName) {
    // oxlint-disable-next-line no-console
    console.warn(
      '[Adobe Express Embed] clientId and appName are required. Set VITE_ADOBE_EXPRESS_CLIENT_ID and VITE_BRANDING_APP_NAME in .env, or pass them to app.use(AdobeExpressEmbedPlugin, { clientId, appName }).',
    );
  }
  app.provide(ADOBE_EXPRESS_EMBED_CONFIG, config);
}

export { ADOBE_EXPRESS_EMBED_CONFIG, SDK_CDN_URL };
export default { install };
